import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface BodyProps {
    message: string;
    ok: boolean;
    action?: { label: string; run: () => void };
    closeToast?: () => void;
}

const ToastBody = ({ message, ok, action, closeToast }: BodyProps) => {
    const Icon = ok ? CheckCircleIcon : ExclamationTriangleIcon;
    return (
        <div className="flex min-h-12 items-center gap-3">
            <Icon className={ok ? 'size-5 shrink-0 text-inverse-ok' : 'size-5 shrink-0 text-inverse-danger'} aria-hidden="true" />
            <span className="flex-1 text-small">{message}</span>
            {action && (
                <button
                    type="button"
                    onClick={() => {
                        action.run();
                        closeToast?.();
                    }}
                    className="min-h-control px-1 text-small font-semibold text-inverse-link underline underline-offset-2"
                >
                    {action.label}
                </button>
            )}
        </div>
    );
};

type RetryAction = { label: string; run: () => void };
let errorListener: ((message: string, retry?: RetryAction) => void) | undefined;

/** The shell's notification bell listens here so every error toast of the session is also listed there. */
export const onNotifyError = (listener: typeof errorListener): void => {
    errorListener = listener;
};

export const notify = {
    /** Past tense of the action ("Saved"). With `undo` the toast also offers the button. */
    success: (message: string, opts?: { undo?: RetryAction }) =>
        toast(<ToastBody message={message} ok action={opts?.undo} />, {
            role: 'status',
        }),
    /** Errors stay until dismissed. */
    error: (message: string, opts?: { retry?: RetryAction }) => {
        errorListener?.(message, opts?.retry);
        return toast(<ToastBody message={message} ok={false} action={opts?.retry} />, { role: 'alert', autoClose: false });
    },
};

/** Mounted once, in Layout. Top-right; a toast goes after 5 s or on click. */
export const Toaster = () => (
    <ToastContainer
        className="qo-toasts"
        toastClassName="!min-h-12 !rounded-control !bg-inverse !px-4 !py-3 !text-on-inverse !shadow-e2 cursor-pointer qo-toast-item"
        position="top-right"
        autoClose={5000}
        hideProgressBar
        closeOnClick
        pauseOnFocusLoss={false}
        closeButton={false}
        newestOnTop
        limit={3}
    />
);
